import { createContext, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
	useLoginAsUserMutation,
	useLoginMutation,
	useLogoutMutation,
} from "./authApiSlice";

import apiSlice from "../../app/api/apiSlice";
import { useGetCurrentUserQuery } from "../user/userApiSlice";

const AuthContext = createContext();

const SHOULD_NOT_FETCH_USER_ON_PATHS = ["/offers/emails"];

export const AuthProvider = ({ children }) => {
	const currentPath = window.location.pathname;
	const dispatch = useDispatch();
	const [login, { isLoading: isSigningIn }] = useLoginMutation();
	const [loginAsUser, { isLoading: isSigningInAsUser }] =
		useLoginAsUserMutation();
	const [loginAsUserId, setLoginAsUserId] = useState(null);
	const [logout, { isLoading: isSigningOut }] = useLogoutMutation();
	const [user, setUser] = useState(null);
	const [loggedIn, setLoggedIn] = useState(null);
	const {
		data: currentUser,
		isSuccess: isCurrentUserSuccess,
		isError: isCurrentUserError,
	} = useGetCurrentUserQuery(undefined, {
		// Keep the query subscribed while we are logged-in (or still deciding).
		// Otherwise invalidated tags (e.g. after profile update) can't refetch.
		skip:
			SHOULD_NOT_FETCH_USER_ON_PATHS.includes(currentPath) ||
			loggedIn === false,
		refetchOnMountOrArgChange: true,
	});

	useEffect(() => {
		if (SHOULD_NOT_FETCH_USER_ON_PATHS.includes(currentPath)) {
			setLoggedIn(false);
			setUser(null);
			return;
		}

		if (isCurrentUserSuccess && currentUser) {
			setUser(currentUser);
			setLoggedIn(true);
			return;
		}

		if (isCurrentUserError) {
			setUser(null);
			setLoggedIn(false);
		}
	}, [currentPath, currentUser, isCurrentUserError, isCurrentUserSuccess]);

	const signIn = async (email, password) => {
		return login({ email, password })
			.unwrap()
			.then((payload) => {
				setUser(payload.user);
				setLoggedIn(true);
				dispatch(
					apiSlice.util.upsertQueryData(
						"getCurrentUser",
						undefined,
						payload.user,
					),
				);
				return payload.user;
			});
	};

	const signInAsUser = async (userId) => {
		setLoginAsUserId(userId);

		const user = await loginAsUser({ userId })
			.unwrap()
			.then((payload) => {
				setUser(payload.user);
				setLoggedIn(true);
				setLoginAsUserId(null);
				dispatch(
					apiSlice.util.upsertQueryData(
						"getCurrentUser",
						undefined,
						payload.user,
					),
				);
				return payload.user;
			});

		return user;
	};

	const signOut = async () => {
		try {
			await logout().unwrap();

			dispatch(apiSlice.util.resetApiState());
			setUser(null);
			setLoggedIn(false);
			setTimeout(() => {
				dispatch(apiSlice.endpoints.getCurrentBranding.initiate());
			}, 300);
		} catch (error) {
			console.log("Error during sign out:", error);
		}
	};

	const value = {
		user,
		loggedIn,
		signIn,
		isSigningIn,
		signOut,
		isSigningOut,
		signInAsUser,
		isSigningInAsUser,
		loginAsUserId,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
